import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  Input,
  inject,
} from "@angular/core";
import { Artikel } from "../../../../formular-model/formular-groups/interfaces/FormularGroupArtikel";
import {
  CstListViewDataSource,
  ICstGuiCommand,
  ICstListViewColumn,
} from "src/app/common-components/list-view/models";
import { CstResponsivePopupService } from "src/app/common-components/resp-popup/services/resp-popup.service";
import { of } from "rxjs";
import { NewOrEditArtikelPopupComponent } from "../popup/popup.component";
import { ItemRequest, ItemResult } from "../artikel.types";
import { EINHEITEN } from "../artikel.types";

@Component({
  selector: "tab-formular-artikel-table",
  templateUrl: "./table.component.html",
  styleUrls: ["./table.component.scss"],
  standalone: false,
})
export class FormularGroupArtikelTableComponent implements AfterContentInit {
  private _popup = inject(CstResponsivePopupService);
  private _cdr = inject(ChangeDetectorRef);

  @Input() data!: {
    data: Artikel[];
    actions: { label: string; value: string | number | null }[];
  };

  public dataSource: CstListViewDataSource<Artikel>;
  public columns: ICstListViewColumn<Artikel>[];
  public actions: ICstGuiCommand<Artikel>[];

  public get itemActionsMap() {
    return this.data.actions;
  }

  private _cmdEditRow: ICstGuiCommand<Artikel> = {
    icon: "edit",
    label: "Edit",
    tooltip: "Edit this row",
    execute: (item) => this._editRow(item),
    canExecute: (item) => this._canEditRow(item),
  };

  private _editRow(item: Artikel): void {
    const index = this.data.data.findIndex((e) => e === item);
    this.onEditItem(index);
  }

  private _canEditRow(_item: Artikel): boolean {
    return true;
  }

  private _getUnitLabel(value: string) {
    const res = EINHEITEN.find((e) => e.value === value);
    return res ? res.label : value;
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  private _cmdRemoveRow: ICstGuiCommand<Artikel> = {
    icon: "delete",
    label: "Remove",
    tooltip: "Remove this row",
    execute: (item) => this._removeRow(item),
    canExecute: (item) => this._canRemoveItem(item),
  };

  private _canRemoveItem(_item: Artikel) {
    return true;
  }

  private _removeRow(_item: Artikel): void {
    this.data.data = this.data.data.filter((item) => item !== _item);
    this.refresh();
  }

  constructor() {
    this.dataSource = new CstListViewDataSource<Artikel>({
      sorting: {
        column: "ArtikelNr",
        direction: "asc",
        enableServerSideSorting: false,
      },
    });

    this.columns = [
      {
        header: "Artikel Nr",
        field: "ArtikelNr",
        width: "15%",
        alignData: "center",
      },
      {
        header: "Artikelbezeichnung",
        field: "Kurztext",
        width: "55%",
        overflow: "wrap",
      },
      {
        header: "Aktion",
        field: "Aktion",
        width: "15%",
        alignData: "center",
        converter: (value) => this._getArtikelAktionName(value),
      },
      {
        header: "Menge",
        field: "Menge",
        width: "5%",
        alignData: "center",
      },
      {
        header: "Einheit",
        field: "Mengeneinheit",
        width: "10%",
        alignData: "center",
        converter: (value) => this._getUnitLabel(value),
      },
    ];

    this.actions = [this._cmdEditRow, this._cmdRemoveRow];
  }

  ngAfterContentInit(): void {
    this.dataSource.registerFetchDataCallback(() =>
      of({ data: this.data?.data, total: this.data.data.length }),
    );

    this.refresh();
  }

  private _getArtikelAktionName(value: string): string {
    return this.itemActionsMap.find((i) => i.value === value)?.label || "";
  }

  public refresh() {
    this.dataSource.refresh();
    this._cdr.detectChanges();
  }

  onEditItem(index: number) {
    const dialogRef = this._popup.open<
      NewOrEditArtikelPopupComponent,
      ItemRequest,
      ItemResult | null
    >(NewOrEditArtikelPopupComponent, {
      data: {
        state: "edit",
        data: this.data.data[index],
        actions: this.itemActionsMap,
      },
    });

    return dialogRef.afterClosed().subscribe({
      next: (result) => this._handleEditItem(index, result as ItemResult),
      error: (error) => this._handleError(error),
    });
  }

  private _handleEditItem(index: number, itemResult: ItemResult) {
    if (!itemResult || !itemResult.state || !itemResult.data) return;

    this.data.data[index] = itemResult.data;
    this.refresh();
  }

  private _handleError(error: any) {
    console.log(error);
  }
}
